/**
 * Extract user-friendly error message from various error response formats
 */
export function extractErrorMessage(error: any): string {
  // Default error message
  const defaultMessage = 'An unexpected error occurred. Please try again.';
  
  if (!error) {
    return defaultMessage;
  }
  
  // Handle auth service error format: { error: { message: "...", code: "..." } }
  if (error.response?.data?.error) {
    const errorObj = error.response.data.error;
    if (typeof errorObj === 'string') {
      return errorObj;
    }
    if (errorObj.message) {
      return errorObj.message;
    }
  }
  
  // Handle simple message format: { message: "..." }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Handle validation errors: { errors: [...] }
  if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
    return error.response.data.errors.join(', ');
  }
  
  // Handle field-specific errors: { error: { field: "message" } }
  if (error.response?.data?.error && typeof error.response.data.error === 'object') {
    const errors = Object.values(error.response.data.error);
    if (errors.length > 0) {
      return errors.join(', ');
    }
  }
  
  // Handle string response data
  if (typeof error.response?.data === 'string') {
    return error.response.data;
  }
  
  // Handle network errors
  if (error.message) {
    if (error.message.includes('timeout')) {
      return 'Connection timed out. Please check your network and try again.';
    }
    if (error.message.includes('Network Error')) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    if (error.code === 'ECONNREFUSED') {
      return 'Unable to connect to the server. Please ensure the service is running.';
    }
    // Return the error message if it seems user-friendly
    if (error.message.length < 100 && !error.message.includes('axios')) {
      return error.message;
    }
  }
  
  // Handle specific HTTP status codes
  if (error.response?.status) {
    switch (error.response.status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication failed. Please check your credentials.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 409:
        return 'This action conflicts with existing data.';
      case 429:
        return 'Too many requests. Please slow down and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
        return 'Bad gateway. The server is temporarily unavailable.';
      case 503:
        return 'Service unavailable. Please try again later.';
      default:
        return `Server error (${error.response.status}). Please try again.`;
    }
  }
  
  return defaultMessage;
}

/**
 * Format error for logging (includes more details)
 */
export function formatErrorForLogging(error: any): object {
  return {
    message: error.message,
    status: error.response?.status,
    statusText: error.response?.statusText,
    data: error.response?.data,
    config: {
      url: error.config?.url,
      method: error.config?.method,
      headers: error.config?.headers,
    },
    stack: error.stack,
  };
}